/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { getMediaSizePositionProps } from '@web-stories-wp/media';

/**
 * Internal dependencies
 */
import { useRef } from '@web-stories-wp/react';
import { useUnits } from '@web-stories-wp/units';
import StoryPropTypes from '../../types';
import {
  elementFillContent,
  elementWithBorder,
  elementWithBackgroundColor,
} from '../shared';
import { useTransformHandler } from '../../components/transform';
import {
  getResponsiveBorder,
  shouldDisplayBorder,
} from '../../utils/elementBorder';
import {
  getOverlayFilterStyle,
  getOverlayRenderStack,
} from '../../utils/overlayStack';
import useColorTransformHandler from '../shared/useColorTransformHandler';
import { getMediaWithScaleCss } from './util';

const Element = styled.div.attrs({ className: 'story-media-display-element' })`
  ${elementFillContent}
  ${({ showPlaceholder }) => showPlaceholder && `background-color: #C4C4C4;`}
  color: transparent;
  overflow: hidden;
  ${elementWithBorder}
`;

const Overlay = styled.div`
  ${elementFillContent}
  ${elementWithBackgroundColor}
`;

function FilterOverlay({ elementId, filter, height, width }) {
  const overlayRef = useRef(null);
  useColorTransformHandler({
    id: elementId,
    targetRef: overlayRef,
    expectedFilterId: filter.id,
    fallbackColor: filter.pattern,
    fallbackBlur: filter.blur,
    resetOnNullTransform: false,
    useOverlayFilterStyle: true,
  });

  return (
    <Overlay
      ref={overlayRef}
      style={getOverlayFilterStyle(filter, { height, width })}
    />
  );
}

FilterOverlay.propTypes = {
  elementId: PropTypes.string.isRequired,
  filter: PropTypes.object.isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
};

function MediaDisplay({
  element,
  mediaRef,
  children,
  previewMode,
  showPlaceholder = false,
}) {
  const {
    id,
    resource,
    scale,
    focalX,
    focalY,
    border,
    borderRadius,
    width,
    height,
    isBackground,
  } = element;
  const overlayFilters = getOverlayRenderStack(element);

  const { dataToEditorX } = useUnits((state) => ({
    dataToEditorX: state.actions.dataToEditorX,
  }));

  const ref = useRef();
  const responsiveBorder = getResponsiveBorder(
    border,
    previewMode,
    dataToEditorX
  );
  useColorTransformHandler({
    id,
    targetRef: ref,
    expectedStyle: 'border-color',
  });
  useTransformHandler(id, (transform) => {
    const target = mediaRef.current;
    if (mediaRef.current) {
      if (transform === null) {
        target.style.cssText = '';
      } else {
        const { resize } = transform;
        if (resize && resize[0] !== 0 && resize[1] !== 0) {
          // @todo this needs to resize the outside border element separately now.
          const newImgProps = getMediaSizePositionProps(
            resource,
            resize[0],
            resize[1],
            scale,
            focalX,
            focalY
          );
          target.style.cssText = getMediaWithScaleCss(newImgProps);
          if (shouldDisplayBorder(element)) {
            ref.current.style.width =
              resize[0] + responsiveBorder.left + responsiveBorder.right + 'px';
            ref.current.style.height =
              resize[1] + responsiveBorder.top + responsiveBorder.bottom + 'px';
          }
        }
      }
    }
  });

  return (
    <Element
      ref={ref}
      data-combined-paint-frame={shouldDisplayBorder(element) ? '' : undefined}
      border={responsiveBorder}
      borderRadius={borderRadius}
      width={width}
      height={height}
      showPlaceholder={showPlaceholder}
    >
      {children}
      {!isBackground &&
        overlayFilters.map((filter) => (
          <FilterOverlay
            key={filter.id}
            elementId={id}
            filter={filter}
            height={height}
            width={width}
          />
        ))}
    </Element>
  );
}

MediaDisplay.propTypes = {
  element: StoryPropTypes.elements.media.isRequired,
  mediaRef: PropTypes.object,
  children: PropTypes.node.isRequired,
  showPlaceholder: PropTypes.bool,
  previewMode: PropTypes.bool,
};

export default MediaDisplay;
